import type { TableRow } from "@general-backoffice/core";
import { FormInput, Loading, TableCard, TableOutput } from "@general-backoffice/core";
import { Modal, notify } from "@general-backoffice/core/index";
import type { ClaimHistoryItem, PaginationMeta, User } from "@investhinks/api-client";
import { saveAs } from "file-saver";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, Col, FormGroup, Row } from "reactstrap";

import { invoicingEndpoints, payoutsEndpoints } from "../../api";
import getErrorMessage from "../../utils/getErrorMessage";
import { notifyLoadingToError, notifyLoadingToSuccess } from "../../utils/notify/notifyLoadingUpdate";
import { TypedTable } from "../../utils/typedLib";
import PaginatedTableFooter from "../shared/PaginatedTableFooter";
import UserSelect from "../users/UserSelect";
import { getCurrencyDisplay } from "../utils/currency";
import handleError from "../utils/handleError";
import { getStatusColor } from "../utils/statusColor";

// Props interfaces
interface ClaimFilters {
  createdAt?: {
    $gte?: string;
    $lte?: string;
  };
  status?: {
    $eq?: string;
  };
  "user.id"?: {
    $eq?: number | string;
  };
  [key: string]: unknown;
}

// Extended claim item with selection state for table
type ExtendedClaimItem = ClaimHistoryItem & {
  isSelected?: boolean;
};

interface PayoutsTableCardProps {
  title?: string;
  claims: ClaimHistoryItem[];
  isLoading: boolean;
  pagination?: PaginationMeta;
  goToPage?: (page: number) => void;
  changePageSize?: (size: number) => void;
  filters?: ClaimFilters;
  setFilters?: (filters: ClaimFilters) => void;
  columnsToShow?: string[];
  filtersToShow?: string[];
  onClaimProcessed?: (claim: ClaimHistoryItem) => void;
}

interface FilterModalProps {
  isOpen: boolean;
  toggle: () => void;
  filters: ClaimFilters;
  setFilters: (filters: ClaimFilters) => void;
  isLoading: boolean;
  filtersToShow: string[];
}

const statusColor: Record<string, string> = {
  pending: "gray",
  processing: "info",
  processed: "success",
  confirmed: "success",
  paid: "success",
  failed: "danger",
  cancelled: "danger",
  expired: "warning",
};

const PayoutsTableCard: React.FC<PayoutsTableCardProps> = ({
  title,
  claims,
  isLoading,
  pagination,
  goToPage,
  changePageSize,
  filters = {},
  setFilters,
  columnsToShow: _columnsToShow = [],
  filtersToShow = [],
  onClaimProcessed,
}) => {
  const { t } = useTranslation("payouts", { keyPrefix: "payoutsTableCard" });
  const { t: tFilters } = useTranslation("charges", { keyPrefix: "filterModal" });
  const [selectedClaimIds, setSelectedClaimIds] = useState<(number | string)[]>([]);
  const [isOpenFilters, setIsOpenFilters] = useState<boolean>(false);
  const [claimingId, setClaimingId] = useState<number | string | null>(null);
  const [cancellingId, setCancellingId] = useState<number | string | null>(null);
  const [cancelConfirmClaim, setCancelConfirmClaim] = useState<ClaimHistoryItem | null>(null);

  const selectedClaims = (claims || []).filter(({ id }) => selectedClaimIds.includes(id));
  const claimsWithIsSelected: ExtendedClaimItem[] = (claims || []).map((claim) => ({
    ...claim,
    isSelected: selectedClaimIds.includes(claim.id),
  }));
  const existsSelectedClaims = selectedClaims.length > 0;

  const onTableChange = (rows: TableRow[] = [], trigger: string) => {
    if (trigger === "selectable") {
      const selected = rows.filter((item) => item.isSelected).map((item) => item.id);
      setSelectedClaimIds(selected);
    }
  };

  const downloadSelectedClaims = async () => {
    const loadingNotification = notify.loading(t("downloadAsPdfs.downloading", "Descargando..."));
    try {
      const blob = await payoutsEndpoints.downloadInvoices(selectedClaimIds.map(Number).filter((n) => !isNaN(n)));
      saveAs(blob, "payout_invoices.zip");
      notifyLoadingToSuccess(loadingNotification, t("downloadAsPdfs.downloaded", "Descargado"));
    } catch {
      notifyLoadingToError(loadingNotification, t("downloadAsPdfs.downloadError", "Error al descargar"));
    }
  };

  const downloadSingleInvoice = async (claim: ClaimHistoryItem) => {
    const loadingNotification = notify.loading(t("downloadAsPdfs.downloading", "Descargando..."));
    try {
      let blob;
      // Check source to determine which API to use
      if (claim._source === "invoice") {
        // New invoice entity - use invoice endpoint
        blob = await invoicingEndpoints.downloadMyInvoicePDF(claim.id);
      } else {
        // Old payout entity - use payout endpoint
        blob = await payoutsEndpoints.downloadInvoice(claim.id);
      }
      saveAs(blob, `invoice_${claim.invoiceNumber}.pdf`);
      notifyLoadingToSuccess(loadingNotification, t("downloadAsPdfs.downloaded", "Descargado"));
    } catch {
      notifyLoadingToError(loadingNotification, t("downloadAsPdfs.downloadError", "Error al descargar"));
    }
  };

  // Claim payout/invoice - gets/regenerates link and opens in new tab
  const handleClaim = async (claim: ClaimHistoryItem) => {
    if (claimingId) return; // Already claiming another

    setClaimingId(claim.id);
    try {
      let response;
      // Use different API based on source
      if (claim._source === "invoice") {
        response = await invoicingEndpoints.getInvoiceClaimLink(claim.id);
      } else {
        response = await payoutsEndpoints.getClaimLink(claim.id);
      }
      const url = response?.data?.url;

      if (url) {
        window.open(url, "_blank");
        onClaimProcessed?.(claim);
      } else {
        handleError(t("claimError", "Failed to get payout link"));
      }
    } catch (err: unknown) {
      handleError(getErrorMessage(err, t("claimError", "Failed to get payout link")));
    } finally {
      setClaimingId(null);
    }
  };

  // Cancel a pending claim — executes after modal confirmation
  const executeCancelClaim = async (claim: ClaimHistoryItem) => {
    setCancelConfirmClaim(null);
    setCancellingId(claim.id);
    const loadingNotification = notify.loading(t("cancellingClaim", "Cancelling claim..."));
    try {
      await invoicingEndpoints.cancelClaim(claim.id);
      notifyLoadingToSuccess(loadingNotification, t("cancelClaimSuccess", "Claim cancelled successfully"));
      onClaimProcessed?.(claim);
    } catch (err: unknown) {
      notifyLoadingToError(loadingNotification, getErrorMessage(err, t("cancelClaimError", "Failed to cancel claim")));
    } finally {
      setCancellingId(null);
    }
  };

  const columns = [
    {
      name: t("date"),
      formattedContent: ({ createdAt }: ExtendedClaimItem) => (
        <span className="text-capitalize">{moment(createdAt).format("DD MMM YYYY")}</span>
      ),
      isTh: true,
    },
    {
      name: t("amount"),
      formattedContent: ({ amount, currency, vatAmount, irpfAmount, _source }: ExtendedClaimItem) => (
        <div>
          <span>
            <b>{parseFloat(String(amount || 0)).toFixed(2)}</b> {getCurrencyDisplay(currency)}
          </span>
          {_source === "invoice" && ((vatAmount || 0) > 0 || (irpfAmount || 0) > 0) && (
            <small className="d-block text-muted">
              {(vatAmount || 0) > 0 && `+${vatAmount?.toFixed(2)} VAT`}
              {(irpfAmount || 0) > 0 && ` -${irpfAmount?.toFixed(2)} IRPF`}
            </small>
          )}
        </div>
      ),
    },
    {
      name: t("number"),
      formattedContent: ({ invoiceNumber }: ExtendedClaimItem) => invoiceNumber,
    },
    {
      name: t("state"),
      formattedContent: ({ status, verifactuStatus, _source }: ExtendedClaimItem) => (
        <div>
          <TableOutput.Status status={t(`status.${status}`)} color={getStatusColor(statusColor, status)} />
          {_source === "invoice" && verifactuStatus && (
            <small className="d-block text-muted mt-1">
              <i className="fas fa-file-signature mr-1" />
              {verifactuStatus}
            </small>
          )}
        </div>
      ),
      isTh: true,
    },
    {
      name: "",
      formattedContent: (claim: ExtendedClaimItem) => {
        const isClaiming = claimingId === claim.id;
        const isPending = claim.status === "pending";
        const isProcessed = claim.status === "processed" || claim.status === "confirmed" || claim.status === "paid";

        return (
          <div className="d-flex align-items-center" style={{ gap: "0.5rem" }}>
            {/* Show TEST button for test invoices - allows download + cancel */}
            {claim.isTest && (
              <>
                <Button
                  size="sm"
                  color="warning"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    downloadSingleInvoice(claim);
                  }}
                  title={t("downloadTestInvoice", "Descargar factura de prueba")}
                >
                  <i className="fas fa-download mr-1" />
                  TEST
                </Button>
                {isPending && claim._source === "invoice" && (
                  <Button
                    size="sm"
                    color="danger"
                    outline
                    disabled={cancellingId === claim.id || !!cancellingId}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      setCancelConfirmClaim(claim);
                    }}
                    title={t("cancelClaim", "Cancel claim")}
                  >
                    {cancellingId === claim.id ? (
                      <i className="fas fa-spinner fa-spin" />
                    ) : (
                      <i className="fas fa-times" />
                    )}
                  </Button>
                )}
              </>
            )}
            {/* Show Claim button for pending payouts/invoices (non-test only) */}
            {!claim.isTest && isPending && (
              <>
                <Button
                  size="sm"
                  color="success"
                  disabled={isClaiming || !!claimingId}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleClaim(claim);
                  }}
                  title={t("claimPayout", "Reclamar pago")}
                >
                  {isClaiming ? (
                    <i className="fas fa-spinner fa-spin mr-1" />
                  ) : (
                    <i className="fas fa-external-link-alt mr-1" />
                  )}
                  {t("claim", "Claim")}
                </Button>
                {claim._source === "invoice" && (
                  <Button
                    size="sm"
                    color="danger"
                    outline
                    disabled={cancellingId === claim.id || !!cancellingId}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      setCancelConfirmClaim(claim);
                    }}
                    title={t("cancelClaim", "Cancel claim")}
                  >
                    {cancellingId === claim.id ? (
                      <i className="fas fa-spinner fa-spin" />
                    ) : (
                      <i className="fas fa-times" />
                    )}
                  </Button>
                )}
              </>
            )}
            {/* Download button for processed payouts */}
            {isProcessed && (
              <Button
                size="sm"
                color="primary"
                outline
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  downloadSingleInvoice(claim);
                }}
                title={t("downloadInvoice", "Descargar factura")}
              >
                <i className="fas fa-download" />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      {/* Cancel claim confirmation modal */}
      <Modal.Simple
        color="danger"
        isOpen={!!cancelConfirmClaim}
        isLoading={!!cancellingId}
        toggleOpen={() => setCancelConfirmClaim(null)}
        closeText={t("close", "Close")}
        buttons={[
          {
            color: "white",
            children: t("cancelClaim", "Cancel claim"),
            onClick: () => cancelConfirmClaim && executeCancelClaim(cancelConfirmClaim),
          },
        ]}
      >
        <div className="py-3 text-center">
          <i className="fas fa-exclamation-triangle ni-3x mb-4" />
          <p>
            {t(
              "cancelClaimConfirm",
              "Are you sure you want to cancel this claim? The Revolut payout link will be invalidated."
            )}
          </p>
        </div>
      </Modal.Simple>

      {setFilters && (
        <FilterModal
          isOpen={isOpenFilters}
          toggle={() => setIsOpenFilters(false)}
          filters={filters}
          setFilters={setFilters}
          isLoading={isLoading}
          filtersToShow={filtersToShow}
        />
      )}

      <Card>
        <TableCard.Header title={title || t("title")} subtitle={t("subtitle")} isLoading={isLoading}>
          {!!filtersToShow.length && setFilters && (
            <div className="d-flex align-items-center" style={{ gap: "1rem" }}>
              {filtersToShow.includes("downloadSelected") && (
                <Button color="default" disabled={!existsSelectedClaims} onClick={downloadSelectedClaims}>
                  <i className="fas fa-file-invoice mr-1" aria-hidden="true" /> {t("downloadSelected")}
                </Button>
              )}
              <Button onClick={() => setIsOpenFilters(true)}>
                <i className="fas fa-filter" aria-hidden="true" /> {tFilters("title")}
              </Button>
            </div>
          )}
        </TableCard.Header>
        {isLoading && <Loading.Line />}

        <TypedTable
          selectable={filtersToShow.includes("downloadSelected")}
          sortable={false}
          onDataChange={onTableChange}
          columns={columns}
          rows={claimsWithIsSelected}
          isLoading={isLoading}
          noContentMessage={t("emptyPayouts")}
          rowHeight={70}
          rowAmount={pagination?.pageSize || 1}
        />

        {pagination && goToPage && changePageSize && (
          <PaginatedTableFooter
            isLoading={isLoading}
            pagination={pagination}
            goToPage={goToPage}
            changePageSize={changePageSize}
            amountOptions={[5, 10, 20, 50, 500]}
          />
        )}
      </Card>
    </>
  );
};


const FilterModal: React.FC<FilterModalProps> = ({ isOpen, toggle, filters, setFilters, isLoading, filtersToShow }) => {
  const { t } = useTranslation("payouts", { keyPrefix: "payoutsTableCard" });
  const { t: tFilters } = useTranslation("charges", { keyPrefix: "filterModal" });

  const [localFilters, setLocalFilters] = useState<ClaimFilters>(filters);
  // The picked user is held as an object for display; only its id goes into
  // the filters the table queries with.
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  useEffect(() => {
    // Sync on open as well as on change: closing without applying must not
    // leave the form showing something other than the filters in force.
    if (!isOpen) return;
    setLocalFilters(filters);
    if (!filters?.user) setSelectedUser(null);
  }, [isOpen, filters]);

  // "Clean" presents as a full reset, so it must clear every filter rather than
  // only the dates and silently leave user/status applied.
  const resetFilters = () => {
    setLocalFilters({});
    setSelectedUser(null);
  };

  const changeNestedFilter = (field: string, operator: string, value: string | undefined) => {
    setLocalFilters((f: ClaimFilters) => {
      // Shallow-copy the nested object too: deleting from it in place mutated
      // the parent's filters object directly.
      const newFilters: ClaimFilters = { ...f };
      if (value === undefined) {
        const fieldValue = newFilters[field];
        if (fieldValue && typeof fieldValue === "object") {
          const fieldObj: Record<string, unknown> = { ...(fieldValue as Record<string, unknown>) };
          delete fieldObj[operator];
          if (Object.keys(fieldObj).length === 0) {
            delete newFilters[field];
          } else {
            newFilters[field] = fieldObj;
          }
        }
      } else {
        const existingField = newFilters[field];
        const existing = typeof existingField === "object" && existingField ? existingField : {};
        newFilters[field] = { ...existing, [operator]: value };
      }
      return newFilters;
    });
  };

  const changeFilter = (key: string, value: unknown) => {
    setLocalFilters((f: ClaimFilters) => ({ ...f, [key]: value }));
  };

  const from = localFilters?.createdAt?.$gte;
  const to = localFilters?.createdAt?.$lte;

  return (
    <Modal.FormContainer
      buttonSave={{
        children: tFilters("applyButton"),
      }}
      closeText={tFilters("closeButton")}
      onSubmit={() => {
        setFilters(localFilters);
        toggle();
      }}
      isOpen={isOpen}
      title={tFilters("title")}
      toggleOpen={toggle}
    >
      <div className="d-flex justify-content-end mb-2 mt--4">
        <Button color="warning" onClick={resetFilters} size="sm">
          <i className="fas fa-broom" aria-hidden="true" /> {tFilters("cleanButton")}
        </Button>
      </div>
      <FormInput.DatePicker
        label={tFilters("chargedAfter")}
        key={from}
        value={from ? moment(from) : undefined}
        onChange={(d: moment.Moment | string) => {
          const newFrom = d && typeof d !== "string" ? d.startOf("day").toISOString() : undefined;
          changeNestedFilter("createdAt", "$gte", newFrom);
        }}
        timeFormat={false}
        dateFormat="DD-MM-YYYY"
        isValidDate={to ? (d: moment.Moment) => d.isSameOrBefore(to) : undefined}
        disabled={isLoading}
      />

      <FormInput.DatePicker
        label={tFilters("chargedBefore")}
        key={to}
        value={to ? moment(to) : undefined}
        onChange={(d: moment.Moment | string) => {
          const newTo = d && typeof d !== "string" ? d.endOf("day").toISOString() : undefined;
          changeNestedFilter("createdAt", "$lte", newTo);
        }}
        timeFormat={false}
        dateFormat="DD-MM-YYYY"
        isValidDate={from ? (d: moment.Moment) => d.isSameOrAfter(from) : undefined}
        disabled={isLoading}
      />

      {filtersToShow.includes("user") && (
        <Row>
          <Col>
            <FormGroup className="mb--4">
              <UserSelect
                label={tFilters("user")}
                user={selectedUser}
                setUser={(user) => {
                  const picked = user && !Array.isArray(user) ? user : null;
                  setSelectedUser(picked);
                  changeFilter("user", picked?.id);
                }}
                isDisabled={isLoading}
                isMulti={false}
                role={undefined}
              />
            </FormGroup>
          </Col>
        </Row>
      )}
      {filtersToShow.includes("failed") && (
        <Row>
          <Col>
            <FormInput.Switch
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                changeFilter("showFailed", e.target.checked || null)
              }
              checked={!!localFilters.showFailed}
              label={t("seAllLabel")}
              labels={t("seAllAnswers", { returnObjects: true })}
              disabled={isLoading}
            />
          </Col>
        </Row>
      )}
    </Modal.FormContainer>
  );
};

export default PayoutsTableCard;
